import React, { useState } from 'react';
import { Box, Typography } from '@mui/joy';

// Folder Tree Item Component
const FolderTreeItem = ({ folder, selectedFolder, onSelect, level = 0, flatFolders = [] }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Get subfolders from flat list instead of relying on children property
    const subfolders = flatFolders.filter(f => f.parent_folder_id === folder.id);
    const hasChildren = subfolders.length > 0;
    const isSelected = selectedFolder === folder.id;


    return (
        <Box sx={{ ml: level * 1.5, mb: 0.5 }}>
            <Box
                sx={{
                    p: 1,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? 'primary.50' : 'transparent',
                    border: isSelected ? '1px solid' : '1px solid transparent',
                    borderColor: isSelected ? 'primary.200' : 'transparent',
                    '&:hover': {
                        backgroundColor: 'neutral.50'
                    }
                }}
                onClick={() => {
                    // Select the folder
                    onSelect(folder.id);
                    // If folder has children, also expand/collapse
                    if (hasChildren) {
                        setIsExpanded(!isExpanded);
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {hasChildren && (
                        <Box sx={{
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'neutral.100', borderRadius: '4px' }
                        }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsExpanded(!isExpanded);
                            }}
                        >
                            <Typography sx={{ fontSize: '14px' }}>
                                {isExpanded ? '📂' : '📁'}
                            </Typography>
                        </Box>
                    )}
                    {!hasChildren && (
                        <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: '14px' }}>📁</Typography>
                        </Box>
                    )}
                    <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: folder.color || '#1976d2',
                        flexShrink: 0
                    }} />
                    <Typography
                        level="body-sm"
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            // Select the folder
                            onSelect(folder.id);
                            // If folder has children, also expand/collapse
                            if (hasChildren) {
                                setIsExpanded(!isExpanded);
                            }
                        }}
                    >
                        {folder.folder_name}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'neutral.500', flexShrink: 0 }}>
                        {folder.content_count || 0}
                    </Typography>
                </Box>
            </Box>

            {hasChildren && isExpanded && (
                <Box sx={{ mt: 0.5 }}>
                    {subfolders.map((child) => (
                        <FolderTreeItem
                            key={child.id}
                            folder={child}
                            selectedFolder={selectedFolder}
                            onSelect={onSelect}
                            level={level + 1}
                            flatFolders={flatFolders}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

// Main Folder Tree Selector Component
const FolderTreeSelector = ({
    folders = [],
    selectedFolder,
    onSelect,
    maxHeight = '150px',
    showRootOption = true,
    rootOptionLabel = 'No Folder (Root)'
}) => {

    return (
        <Box sx={{
            border: '1px solid',
            borderColor: 'neutral.300',
            borderRadius: '8px',
            p: 2,
            maxHeight,
            overflowY: 'auto',
            backgroundColor: 'neutral.50'
        }}>
            {/* Root Option */}
            {showRootOption && (
                <Box
                    sx={{
                        p: 1,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        backgroundColor: selectedFolder === null ? 'primary.50' : 'transparent',
                        border: selectedFolder === null ? '1px solid' : '1px solid transparent',
                        borderColor: selectedFolder === null ? 'primary.200' : 'transparent',
                        '&:hover': {
                            backgroundColor: 'neutral.100'
                        }
                    }}
                    onClick={() => onSelect(null)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>🏠</Typography>
                        <Typography level="body-sm" sx={{ flex: 1 }}>
                            {rootOptionLabel}
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Folder Tree */}
            {folders.filter(folder => !folder.parent_folder_id).map((folder) => (
                <FolderTreeItem
                    key={folder.id}
                    folder={folder}
                    selectedFolder={selectedFolder}
                    onSelect={onSelect}
                    level={0}
                    flatFolders={folders}
                />
            ))}
        </Box>
    );
};

export default FolderTreeSelector;
